import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Microscope, Info } from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import GeoGebraSimulation from '../../components/interactive/shared/simulations/GeoGebraSimulation'

/**
 * Trang Test GeoGebra Integration
 * 
 * Mục đích:
 * - Test tích hợp GeoGebra vào hệ thống
 * - Demo file .ggb mẫu (Định luật Boyle)
 * - Kiểm tra performance và compatibility
 */
const TestGeoGebra: React.FC = () => {
    const handleReady = (applet: any) => {
        console.log('✅ GeoGebra applet ready:', applet)

        // Demo: Lấy danh sách objects trong file .ggb
        try {
            const objectCount = applet.getObjectNumber()
            console.log('📊 Number of objects in .ggb file:', objectCount)

            // Log tất cả objects
            for (let i = 0; i < objectCount; i++) {
                const objName = applet.getObjectName(i)
                const objType = applet.getObjectType(objName)
                console.log(`  - Object ${i}: ${objName} (type: ${objType})`)
            }
        } catch (err) {
            console.warn('Could not get object info:', err)
        }
    }

    const handleError = (error: Error) => {
        console.error('❌ GeoGebra error:', error)
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
            <Navigation />

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <Link
                        to="/"
                        className="inline-flex items-center text-green-600 hover:text-green-700 mb-4"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Về trang chủ
                    </Link>

                    <div className="flex items-center space-x-4 mb-4">
                        <div className="p-3 bg-gradient-to-r from-green-500 to-teal-600 rounded-xl shadow-lg">
                            <Microscope className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                Test GeoGebra Integration
                            </h1>
                            <p className="text-gray-600">
                                Demo tích hợp mô phỏng GeoGebra vào AI-PhysicsBook
                            </p>
                        </div>
                    </div>

                    {/* Info Card */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-1" />
                        <div className="text-sm text-blue-900">
                            <p className="font-semibold mb-1">Thông tin Test:</p>
                            <ul className="list-disc list-inside space-y-1 text-blue-800">
                                <li>File demo: <code className="bg-blue-100 px-1 rounded">dl_Boyle.ggb</code> (~55KB)</li>
                                <li>GeoGebra Library: Tải từ CDN (production sẽ cache local)</li>
                                <li>Tính năng: Tương tác, Play/Pause, Reset</li>
                                <li>So sánh: Video ~10-50MB → GeoGebra ~55KB (nhẹ hơn 100-1000 lần!)</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* GeoGebra Demo */}
                <div className="space-y-8">
                    {/* Test 1: File có sẵn - Định luật Boyle */}
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">
                            📌 Test 1: Định luật Boyle
                        </h2>
                        <GeoGebraSimulation
                            filename="/geogebra/dl_Boyle.ggb"
                            title="Định luật Boyle - Quan hệ P và V"
                            description="Mô phỏng quan hệ giữa áp suất (P) và thể tích (V) của khí lý tưởng ở nhiệt độ không đổi"
                            height={500}
                            showCustomControls={true}
                            onReady={handleReady}
                            onError={handleError}
                        />
                    </div>

                    {/* Thống kê so sánh */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                            📊 So sánh Video vs GeoGebra
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Video */}
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <h4 className="font-semibold text-red-900 mb-2">
                                    🎥 Video (.mp4)
                                </h4>
                                <ul className="text-sm text-red-800 space-y-1">
                                    <li>✅ Dễ tạo (screen recording)</li>
                                    <li>✅ Tương thích tốt</li>
                                    <li>❌ Dung lượng lớn (10-50MB)</li>
                                    <li>❌ Không tương tác</li>
                                    <li>❌ Không thay đổi tham số</li>
                                    <li>❌ Tốn băng thông</li>
                                </ul>
                            </div>

                            {/* GeoGebra */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <h4 className="font-semibold text-green-900 mb-2">
                                    🎮 GeoGebra (.ggb)
                                </h4>
                                <ul className="text-sm text-green-800 space-y-1">
                                    <li>✅ Dung lượng siêu nhẹ (50-100KB)</li>
                                    <li>✅ Tương tác thời gian thực</li>
                                    <li>✅ Thay đổi tham số dễ dàng</li>
                                    <li>✅ Offline-ready (PWA)</li>
                                    <li>⚠️ Cần library ~5MB (cache 1 lần)</li>
                                    <li>⚠️ Cần test mobile</li>
                                </ul>
                            </div>

                            {/* Kết luận */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="font-semibold text-blue-900 mb-2">
                                    💡 Kết luận
                                </h4>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    <li>✅ GeoGebra phù hợp cho mô phỏng tương tác</li>
                                    <li>✅ Nhẹ hơn 100-1000 lần so với video</li>
                                    <li>✅ Học sinh có thể thử nghiệm</li>
                                    <li>✅ Có thể kết hợp cả 2 (Video cho bài giảng, .ggb cho thực hành)</li>
                                    <li>🎯 Khuyến nghị: Chuyển dần sang GeoGebra</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Hướng dẫn tạo file .ggb */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                            📝 Hướng dẫn tạo file .ggb
                        </h3>

                        <div className="prose max-w-none">
                            <ol className="space-y-3 text-gray-700">
                                <li>
                                    <strong>Bước 1:</strong> Tải GeoGebra Classic từ{' '}
                                    <a
                                        href="https://www.geogebra.org/download"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-green-600 hover:text-green-700"
                                    >
                                        geogebra.org
                                    </a>
                                </li>
                                <li>
                                    <strong>Bước 2:</strong> Thiết kế thí nghiệm (ẩn trục tọa độ, grid nếu không cần)
                                </li>
                                <li>
                                    <strong>Bước 3:</strong> Lưu thành file .ggb (File → Save)
                                </li>
                                <li>
                                    <strong>Bước 4:</strong> Copy file vào thư mục{' '}
                                    <code className="bg-gray-100 px-2 py-1 rounded">geogebra/</code>
                                </li>
                                <li>
                                    <strong>Bước 5:</strong> Sử dụng component{' '}
                                    <code className="bg-gray-100 px-2 py-1 rounded">GeoGebraSimulation</code>
                                </li>
                            </ol>

                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                                <p className="text-sm font-semibold text-gray-900 mb-2">
                                    💡 Tips tạo mô phỏng đẹp:
                                </p>
                                <ul className="text-sm text-gray-700 space-y-1">
                                    <li>• Ẩn hết trục tọa độ, lưới nếu không cần</li>
                                    <li>• Chỉ để lại vật thể vật lý (con lắc, vật chuyển động, đồ thị...)</li>
                                    <li>• Sử dụng slider cho các tham số có thể thay đổi</li>
                                    <li>• Đặt animation cho slider để tạo chuyển động</li>
                                    <li>• Test kỹ trước khi export</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Next Steps */}
                    <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-xl p-6 text-white">
                        <h3 className="text-xl font-bold mb-3">
                            🚀 Các bước tiếp theo
                        </h3>
                        <ul className="space-y-2">
                            <li>✅ Component GeoGebraSimulation đã sẵn sàng</li>
                            <li>✅ Test với file dl_Boyle.ggb thành công</li>
                            <li>⏳ Tích hợp vào trang XemBaiHoc (Tab Mô phỏng)</li>
                            <li>⏳ Thêm cấu hình để chọn Video hoặc GeoGebra</li>
                            <li>⏳ Tạo thêm file .ggb cho các bài học khác</li>
                            <li>⏳ Tối ưu: Cache GeoGebra library cho offline PWA</li>
                        </ul>
                    </div>
                </div>

                {/* Debug Console */}
                <div className="mt-8 bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-sm">
                    <p className="mb-2">💻 Console Debug (Mở DevTools để xem chi tiết):</p>
                    <ul className="space-y-1 text-xs">
                        <li>• Kiểm tra object count trong .ggb file</li>
                        <li>• Test các hàm GeoGebra API</li>
                        <li>• Monitor performance</li>
                    </ul>
                </div>
            </div>
        </div>
    )
}

export default TestGeoGebra
